import { Injectable } from '@angular/core';
import { catchError, map, of, switchMap } from 'rxjs';
import { OpenQuoteModel, PlanModel } from '../core';
import { ErrorHandlingService } from '../core/services';
import { OpenQuoteService } from './open-quote.service';

export interface PromoteAddonParams {
  quote: OpenQuoteModel;
  orderedAddons: PlanModel[];
  effectiveDate: Date;
}

export interface AncillaryTransitionResult {
  quote: OpenQuoteModel | null;
  promoted: PlanModel;
  remainingAddons: PlanModel[];
}

@Injectable({
  providedIn: 'root',
})
export class AncillaryTransitionService {
  constructor(
    private readonly openQuoteService: OpenQuoteService,
    private readonly errorHandlingService: ErrorHandlingService
  ) {}

  promoteAddonToMainPlan(params: PromoteAddonParams) {
    const { quote, orderedAddons, effectiveDate } = params;
    const quoteId = quote?.identifier;
    if (!quoteId) {
      return of(null);
    }

    const promotion = this.selectFirstAddon(orderedAddons);
    if (!promotion) {
      return of(null);
    }

    this.applyAncillaryPromotion(quote, promotion.promoted, promotion.remainingAddons);

    const planIdentifiers = this.getAddonIdentifiers(promotion.remainingAddons);

    return this.openQuoteService.createProposal(quoteId, { planIdentifiers, effectiveDate }).pipe(
      catchError((error) => {
        this.errorHandlingService.handleError(error, 'Error updating proposal.');
        return of(null);
      }),
      switchMap((proposalResponse) => {
        if (!proposalResponse) {
          return of(null);
        }
        return this.openQuoteService.saveQuote(quote).pipe(
          switchMap((response: { quote: OpenQuoteModel }) => {
            const savedQuote = response?.quote ?? null;
            if (!savedQuote) {
              return of(null);
            }
            return this.openQuoteService.getQuote(quoteId).pipe(
              map((latestQuote: OpenQuoteModel) => ({
                quote: latestQuote ?? savedQuote,
                promoted: promotion.promoted,
                remainingAddons: promotion.remainingAddons,
              })),
              catchError(() =>
                of({
                  quote: savedQuote,
                  promoted: promotion.promoted,
                  remainingAddons: promotion.remainingAddons,
                })
              )
            );
          }),
          catchError((error) => {
            this.errorHandlingService.handleError(error, 'Error saving quote.');
            return of(null);
          })
        );
      })
    );
  }

  private selectFirstAddon(addons: PlanModel[]) {
    const validAddons = (addons || []).filter((addon) => !!addon?.identifier);
    if (validAddons.length === 0) {
      return null;
    }
    const [promoted, ...remainingAddons] = validAddons;
    return { promoted, remainingAddons };
  }

  private applyAncillaryPromotion(quote: OpenQuoteModel, promoted: PlanModel, remainingAddons: PlanModel[]): void {
    quote.plans = [promoted];
    quote.planId = promoted.identifier;
    quote.planIdentifier = promoted.identifier;
    quote.class = 'Ancillary';
    quote.addons = [...remainingAddons];

    if (quote.member) {
      quote.member = { ...quote.member, subsidy: null } as any;
    }
  }

  private getAddonIdentifiers(addons: PlanModel[]): string[] {
    return Array.from(
      new Set(
        (addons || [])
          .map((plan) => this.getPlanIdentifier(plan))
          .filter((identifier): identifier is string => !!identifier)
      )
    );
  }

  private getPlanIdentifier(plan: PlanModel | null | undefined): string | null {
    if (!plan) {
      return null;
    }

    const raw = (plan as any).identifier ?? (plan as any).planID ?? (plan as any).planId;
    if (raw == null) {
      return null;
    }

    const value = String(raw).trim();
    return value.length > 0 ? value : null;
  }
}
